/**
 * SMS Communications Hook
 * Manages SMS queue and template data fetching and mutations
 */

import { useState, useCallback } from 'react';
import { useSmsService, useTemplateService } from '@actschurch/ccms-sdk';
import type { AppSMS, AppSMSModelView } from '@actschurch/ccms-sdk';
import type { AppSMSTemplateModelView } from '@actschurch/ccms-sdk';
import { toast } from '../../../../../components/ui/providers/AlertProvider';

export function useSmsCommunications() {
  const smsService = useSmsService();
  const templateService = useTemplateService();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queueItems, setQueueItems] = useState<AppSMSModelView[]>([]);
  const [templates, setTemplates] = useState<AppSMSTemplateModelView[]>([]);

  // ==================== QUEUE OPERATIONS ====================

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await smsService.getAll();
      setQueueItems(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch SMS queue';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [smsService]);

  const fetchQueueItem = useCallback(
    async (id: number): Promise<AppSMSModelView> => {
      try {
        return await smsService.getById(id);
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch SMS ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [smsService]
  );

  const deleteQueueItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(smsService.delete(id), {
          loading: 'Deleting SMS...',
          success: 'SMS deleted successfully',
          error: (err) => `Failed to delete: ${err.message}`,
        });
        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete SMS');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue]
  );

  /**
   * Deletes the whole selection in ONE request, rather than one call per message.
   */
  const bulkDeleteQueueItems = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;

      setLoading(true);
      try {
        const result = await toast.promise(smsService.deleteMany(ids), {
          loading: `Deleting ${ids.length} SMS message(s)...`,
          success: (res) =>
            res.notFoundIds.length > 0
              ? `Deleted ${res.deleted} of ${ids.length} (${res.notFoundIds.length} already gone)`
              : `${res.deleted} SMS message(s) deleted successfully`,
          error: (err) => `Failed to delete: ${err.message}`,
        });
        await fetchQueue();
        return result;
      } catch (err: any) {
        setError(err?.message || 'Failed to delete SMS messages');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue]
  );

  const retryFailedItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const item = queueItems.find((q) => q.messageID === id);
        if (!item) throw new Error('SMS not found');

        await smsService.update(id, {
          ...item,
          status: 'Pending',
          retryCount: 0,
        } as any);

        await fetchQueue();
        toast.success('SMS queued for retry');
      } catch (err: any) {
        setError(err?.message || 'Failed to retry SMS');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue, queueItems]
  );

  const updatePriority = useCallback(
    async (id: number, priority: number) => {
      setLoading(true);
      try {
        const item = queueItems.find((q) => q.messageID === id);
        if (!item) throw new Error('SMS not found');

        await toast.promise(
          smsService.update(id, { ...item, priority } as any),
          {
            loading: 'Updating priority...',
            success: 'Priority updated successfully',
            error: (err) => `Failed to update priority: ${err.message}`,
          }
        );

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update priority');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue, queueItems]
  );

  const updateQueueItem = useCallback(
    async (id: number, data: Partial<AppSMS>) => {
      setLoading(true);
      try {
        await toast.promise(smsService.update(id, data), {
          loading: 'Updating SMS...',
          success: 'SMS updated successfully',
          error: (err) => `Failed to update: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update SMS');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue]
  );

  const createSms = useCallback(
    async (data: Partial<AppSMS>) => {
      setLoading(true);
      try {
        await toast.promise(smsService.create(data), {
          loading: 'Queuing SMS...',
          success: 'SMS queued successfully',
          error: (err) => `Failed to queue SMS: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to create SMS');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smsService, fetchQueue]
  );

  // ==================== TEMPLATE OPERATIONS ====================

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await templateService.getSmsTemplates();
      setTemplates(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch SMS templates';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [templateService]);

  const fetchTemplate = useCallback(
    async (id: number): Promise<AppSMSTemplateModelView> => {
      try {
        return await templateService.getSmsTemplate(id);
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch template ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [templateService]
  );

  const createTemplate = useCallback(
    async (data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.createSmsTemplate(data), {
          loading: 'Creating template...',
          success: 'Template created successfully',
          error: (err) => `Failed to create template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to create template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (id: number, data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.updateSmsTemplate(id, data), {
          loading: 'Updating template...',
          success: 'Template updated successfully',
          error: (err) => `Failed to update template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to update template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const deleteTemplate = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(templateService.deleteSmsTemplate(id), {
          loading: 'Deleting template...',
          success: 'Template deleted successfully',
          error: (err) => `Failed to delete template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  // ==================== COMBINED FETCH ====================

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchQueue(), fetchTemplates()]);
  }, [fetchQueue, fetchTemplates]);

  return {
    loading,
    error,
    setError,

    // Queue
    queueItems,
    fetchQueue,
    fetchQueueItem,
    deleteQueueItem,
    bulkDeleteQueueItems,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
    createSms,

    // Templates
    templates,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Combined
    fetchAll,
  };
}
